import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Message } from "@ag-ui/client";
import IngestionStatus from "datasets/pages/tables/types/enums/ingest-status";
import {
  SessionContextPanelType,
  useSessionContextPanel,
} from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import { apiClient } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

// create_ingest surfaces under the data-prep toolkit's `data_prep` MCP prefix
// (cf. ConfirmIngestCard's PROFILE_TOOL_NAME), so this is the name it carries in
// the turn's tool calls.
const CREATE_INGEST_TOOL_NAME = "data_prep_create_ingest";

// completed/failed are terminal and never change again; everything else
// (draft/pending/started) is still in flight.
const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  IngestionStatus.COMPLETED,
  IngestionStatus.FAILED,
]);

export interface SessionIngest {
  // Catalog ingest job id, recovered from the create_ingest tool result.
  jobId: string;
  // Target table FQN from that same result — the human-facing label for the TOC
  // row and the detail switcher, so neither needs a catalog poll. `null` when
  // the result carried no table.
  tableFqn: string | null;
}

interface SessionIngestsContextValue {
  ingests: SessionIngest[];
  setIngests: (ingests: SessionIngest[]) => void;
}

// Holds the session's ingestion list above both `Chat` (which owns the messages
// it is derived from) and `SessionContextSidebar` (which renders it) — the two
// are siblings, so the shared state lives in their common ancestor,
// `ProjectLayout`. Mirrors `McpAppsPaneProvider`.
const SessionIngestsContext = createContext<SessionIngestsContextValue | null>(null);

export const SessionIngestsProvider = ({ children }: { children: React.ReactNode }) => {
  const [ingests, setIngests] = useState<SessionIngest[]>([]);

  return (
    <SessionIngestsContext.Provider value={{ ingests, setIngests }}>
      {children}
    </SessionIngestsContext.Provider>
  );
};

// Recover the ingest job id and target table from a create_ingest tool result.
// The tool returns a flat IngestJob dict, so both live at the top level
// (design A1): `JSON.parse(result).id` and `.table_fqn`.
function parseIngestResult(raw: string): { id: string; tableFqn: string | null } | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const rec = parsed as { id?: unknown; table_fqn?: unknown };
      if (typeof rec.id === "string" && rec.id.length > 0) {
        const tableFqn =
          typeof rec.table_fqn === "string" && rec.table_fqn.length > 0 ? rec.table_fqn : null;
        return { id: rec.id, tableFqn };
      }
    }
  } catch {
    // Not JSON — nothing to recover.
  }
  return null;
}

// Membership is a pure function of conversation history: every create_ingest
// result across all turns contributes its job id, de-duped, in first-seen order.
// Catalog is never consulted here (it has no notion of an AI session); it
// supplies only per-row state downstream, via useIngestStatus.
export function sessionIngestsFromMessages(messages: ReadonlyArray<Message>): SessionIngest[] {
  const resultByCallId = new Map<string, string>();
  for (const m of messages) {
    const tm = m as { role?: string; toolCallId?: string; content?: string };
    if (tm.role === "tool" && tm.toolCallId && typeof tm.content === "string") {
      resultByCallId.set(tm.toolCallId, tm.content);
    }
  }

  const seen = new Set<string>();
  const ingests: SessionIngest[] = [];
  for (const m of messages) {
    const calls = (m as { toolCalls?: { id: string; function: { name: string } }[] }).toolCalls;
    if (!calls) {
      continue;
    }
    for (const tc of calls) {
      if (tc.function.name !== CREATE_INGEST_TOOL_NAME) {
        continue;
      }
      const raw = resultByCallId.get(tc.id);
      if (raw === undefined) {
        continue;
      }
      const parsed = parseIngestResult(raw);
      if (!parsed || seen.has(parsed.id)) {
        continue;
      }
      seen.add(parsed.id);
      ingests.push({ jobId: parsed.id, tableFqn: parsed.tableFqn });
    }
  }
  return ingests;
}

async function isIngestInFlight(jobId: string, signal: AbortSignal): Promise<boolean> {
  const resp = await apiClient<{ status: string }>(`${VITE_CATALOG_API}/ingests/${jobId}`, {
    signal,
  });
  return !TERMINAL_STATUSES.has(resp.status);
}

// Called from `Chat`. Publishes the derived membership into the shared context
// and drives the panel's open/focus:
//   - a create_ingest that appears *after* mount is a live event → open + focus
//     its row immediately (no catalog notion of "session", so the streamed
//     result is the signal — there is no custom AG-UI event to lean on);
//   - ids already present at mount are a reload → focus the most recent one only
//     if a one-shot status poll shows it is still in flight, otherwise stay
//     passive. The list itself stays visible in the TOC either way.
export function useSessionIngestsSync(messages: ReadonlyArray<Message>): void {
  const ctx = useContext(SessionIngestsContext);
  const { openPanel } = useSessionContextPanel();
  const seenRef = useRef<Set<string>>(new Set());
  const mountCapturedRef = useRef(false);

  const ingests = useMemo(() => sessionIngestsFromMessages(messages), [messages]);

  // Bridge the derived list into the shared context so the sibling sidebar/TOC
  // can render it. (Cross-tree sharing, not derive-into-state: the consumers are
  // siblings of `Chat`, so the value cannot simply be computed where it's read.)
  useEffect(() => {
    ctx?.setIngests(ingests);
  }, [ctx, ingests]);

  useEffect(() => {
    // First pass: everything already present is the reload set. Record it
    // without opening, then focus the most recent only if it's still loading.
    if (!mountCapturedRef.current) {
      mountCapturedRef.current = true;
      ingests.forEach((i) => seenRef.current.add(i.jobId));
      const last = ingests[ingests.length - 1];
      if (!last) {
        return;
      }
      const controller = new AbortController();
      isIngestInFlight(last.jobId, controller.signal)
        .then((inFlight) => {
          if (!controller.signal.aborted && inFlight) {
            openPanel(SessionContextPanelType.INGEST, last.jobId);
          }
        })
        .catch(() => {
          // Status unknown (transient failure / job gone) → don't steal focus;
          // the row still shows in the TOC and its own poll will catch up.
        });
      return () => controller.abort();
    }

    // Later passes: a genuinely new id is a live create_ingest → open + focus it
    // (last one wins if several land at once).
    const fresh = ingests.filter((i) => !seenRef.current.has(i.jobId));
    if (fresh.length === 0) {
      return;
    }
    fresh.forEach((i) => seenRef.current.add(i.jobId));
    openPanel(SessionContextPanelType.INGEST, fresh[fresh.length - 1].jobId);
  }, [ingests, openPanel]);
}

// Read-only projection of the session's ingestion list — what
// `SessionContextSidebar`'s Ingestion section and detail view render from.
export function useSessionIngests(): SessionIngest[] {
  const ctx = useContext(SessionIngestsContext);
  if (!ctx) {
    throw new Error("useSessionIngests must be used within a SessionIngestsProvider");
  }
  return ctx.ingests;
}
